import { Dices, Gamepad, Sparkles, Trophy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { teams } from '@/config/teams';
import type { Team } from '@/config/teams';

interface TeamProgressProps {
  teams: Record<string, {
    level: number;
  }>;
  submissionQueue?: Array<{
    teamId: string;
    validated?: boolean;
  }>;
  teamColors: Record<number, {
    primary: string;
    secondary: string;
    text: string;
  }>;
  isFeminineMode: boolean;
  getTeamName: (team: any) => string;
}

export function TeamProgress({ teams: teamStates, submissionQueue = [], teamColors, isFeminineMode, getTeamName }: TeamProgressProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {teams.map((team, index) => {
        const teamState = teamStates[team.id] || { level: 0 };
        const currentTeamColor = teamColors[index] || teamColors[0];

        return (
          <Card key={team.id} className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-24">
                {/* Team Icon */}
                {team.icon === 'gamepad' && <Gamepad className={`h-6 w-6 ${currentTeamColor.text}`} />}
                {team.icon === 'sparkles' && <Sparkles className={`h-6 w-6 ${currentTeamColor.text}`} />}
                {team.icon === 'trophy' && <Trophy className={`h-6 w-6 ${currentTeamColor.text}`} />}
                
                {/* Team Name */}
                <span className="font-medium">
                  {getTeamName(team)}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="flex-1 flex items-center">
                <div className="flex-1 flex gap-1 relative">
                  {[...Array(6)].map((_, i) => {
                    const teamLevel = teamState.level || 0;
                    const isCompleted = i < teamLevel;
                    const isCurrent = i === teamLevel;

                    const isPending = submissionQueue?.some(
                      (sub) => sub.teamId === team.id && !sub.validated
                    );

                    return (
                      <div key={i} className="relative flex-1">
                        <div className={cn(
                          "h-16 w-full rounded-lg transition-all duration-300 relative",
                          isCompleted ? currentTeamColor.primary :
                          isCurrent ? currentTeamColor.secondary :
                          "bg-gray-200",
                          isPending && isCurrent && "animate-pulse"
                        )}>
                          {/* Step Content */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            {i === 5 ? (
                              <Trophy className={cn(
                                "w-6 h-6",
                                isCompleted ? "text-white" : 
                                isCurrent ? "text-white" : 
                                "text-gray-400"
                              )} />
                            ) : i === 0 ? (
                              <Dices className={cn(
                                "w-6 h-6",
                                isCompleted ? "text-white opacity-50" :
                                isCurrent ? "text-white animate-bounce" :
                                "text-gray-400"
                              )} />
                            ) : (
                              <span className={cn(
                                "font-bold",
                                isCompleted ? "text-white opacity-50" :
                                isCurrent ? "text-white" :
                                "text-gray-400"
                              )}>
                                {i}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
